import { useMemo } from 'react';
import { useEdge, EdgeStatus } from './EdgeContext';

/**
 * edgeApi — thin typed client for the local edge till agent (same origin).
 *
 * Every mutating endpoint is token-gated with the per-boot session token from
 * EdgeContext (X-Edge-Token header). Calls never throw on HTTP errors: they
 * resolve to an EdgeApiResult with { ok, status, body } so screens can react
 * to specific statuses (e.g. a 409 refund carrying the server's remaining
 * refundable amount). A network-level failure resolves to status 0.
 */

export interface EdgeApiResult<T = unknown> {
  ok: boolean;
  /** HTTP status, or 0 when the agent could not be reached at all. */
  status: number;
  /** Parsed JSON body (success payload OR error payload), null if none. */
  body: T | null;
}

/** Error payloads the agent may return on 4xx (fields optional). */
export interface EdgeErrorBody {
  message?: string;
  /** On refund 409: the server-side remaining refundable amount. */
  remaining?: number;
}

export interface EdgeRecentOrder {
  orderUuid: string;
  billNumber: string;
  total: number;
  tender: string;
  settledAt: string;
  refundedTotal: number;
  remaining: number;
}

/** Z-report payload returned by POST /edge/cash/shift-close. */
export interface EdgeZReport {
  openingFloat: number;
  cashSales: number;
  paidIn: number;
  paidOut: number;
  expectedCash: number;
  countedCash: number;
  variance: number;
}

export interface EdgeApi {
  drawerOpen(req: { float?: number; reason?: string }): Promise<EdgeApiResult<EdgeErrorBody>>;
  paidIn(req: { amount: number; reason: string }): Promise<EdgeApiResult<EdgeErrorBody>>;
  paidOut(req: { amount: number; reason: string }): Promise<EdgeApiResult<EdgeErrorBody>>;
  noSale(req: { reason?: string }): Promise<EdgeApiResult<EdgeErrorBody>>;
  shiftClose(req: {
    countedCash: number;
    notes?: string;
  }): Promise<EdgeApiResult<EdgeZReport & EdgeErrorBody>>;
  recentOrders(limit?: number): Promise<EdgeApiResult<EdgeRecentOrder[]>>;
  refund(
    orderUuid: string,
    req: { amount: number; tender: string; reason: string },
  ): Promise<EdgeApiResult<EdgeErrorBody>>;
  statusz(): Promise<EdgeApiResult<EdgeStatus>>;
}

type GetToken = () => string | null;

async function request<T>(
  getToken: GetToken,
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
): Promise<EdgeApiResult<T>> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['X-Edge-Token'] = token;

  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  try {
    const res = await fetch(path, init);
    let parsed: T | null = null;
    try {
      parsed = (await res.json()) as T;
    } catch {
      // No / non-JSON body — keep null; status still tells the story.
    }
    return { ok: res.ok, status: res.status, body: parsed };
  } catch {
    return { ok: false, status: 0, body: null };
  }
}

export function createEdgeApi(getToken: GetToken): EdgeApi {
  return {
    drawerOpen: req => request(getToken, 'POST', '/edge/cash/drawer-open', req),
    paidIn: req => request(getToken, 'POST', '/edge/cash/paid-in', req),
    paidOut: req => request(getToken, 'POST', '/edge/cash/paid-out', req),
    noSale: req => request(getToken, 'POST', '/edge/cash/no-sale', req),
    shiftClose: req => request(getToken, 'POST', '/edge/cash/shift-close', req),
    recentOrders: (limit = 20) =>
      request(getToken, 'GET', `/edge/orders/recent?limit=${limit}`),
    refund: (orderUuid, req) =>
      request(getToken, 'POST', `/edge/orders/${orderUuid}/refund`, req),
    statusz: () => request(getToken, 'GET', '/edge/statusz'),
  };
}

/** Hook flavour — binds the client to the EdgeContext session token. */
export function useEdgeApi(): EdgeApi {
  const { getToken } = useEdge();
  return useMemo(() => createEdgeApi(getToken), [getToken]);
}

export default createEdgeApi;
